import dayjs from 'dayjs';
import React from 'react';

import { Grid, Form, DateInput, Tooltip } from '@mairistem/composants';

import { Section, SectionHeader } from '@/components/Common';
import { OPTIONS_ETAT_MATRIMONIAL } from '@/constants';

interface InformationsComplementairesProps {
  numeroPartenaire: number;
  identiteVerifiee?: boolean;
  protectionOfpra?: boolean;
  protectionJuridique?: boolean;
  dejaPacse?: boolean;
  etatMatrimonialCode?: string;
  nombreEnfants?: number;
  dateDivorce?: string;
  dateVeuvage?: string;
  inseeObligatoire?: boolean;
  identiteVerifieeObligatoire?: boolean;
  errors: Record<string, any>;
  onChange: (
    _e: any,
    data: { name: string; value?: any; checked?: boolean },
  ) => void;
}

const InformationsComplementaires = (
  props: InformationsComplementairesProps,
) => {
  const {
    numeroPartenaire,
    protectionOfpra,
    protectionJuridique,
    identiteVerifiee,
    dejaPacse,
    etatMatrimonialCode,
    nombreEnfants,
    dateDivorce,
    dateVeuvage,
    inseeObligatoire = false,
    identiteVerifieeObligatoire = true,
    errors,
    onChange,
  } = props;

  const selectedEtatMatrimonial = etatMatrimonialCode || null;
  const inseePrefix = numeroPartenaire === 1 ? 'p1' : 'p2';
  const testIdPrefix = `assistant-partenaire-${numeroPartenaire}-autres-infos`;

  const handlePartenaireChange = React.useCallback(
    (field: string, value: boolean) => {
      onChange(null, {
        name: field,
        value,
      });
    },
    [onChange],
  );

  const handleComplementInseeChange = React.useCallback(
    (field: string, value: string | number | boolean | null) => {
      onChange(null, {
        name: `complementInsee.${inseePrefix}${field}`,
        value,
      });
    },
    [inseePrefix, onChange],
  );

  const handleNombreEnfantsChange = React.useCallback(
    (value: string) => {
      onChange(null, {
        name: 'complementInsee.nombreEnfants',
        value: value === '' ? null : Number(value),
      });
    },
    [onChange],
  );

  const handleInseeDateChange = React.useCallback(
    (field: 'DateDivorce' | 'DateVeuvage', rawValue: string | Date | null) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        handleComplementInseeChange(field, null);
        return;
      }

      const parsedDate =
        rawValue instanceof Date ? rawValue : new Date(rawValue);
      const nextValue = Number.isNaN(parsedDate.getTime())
        ? null
        : dayjs(parsedDate).format('YYYY-MM-DD');

      handleComplementInseeChange(field, nextValue);
    },
    [handleComplementInseeChange],
  );

  return (
    <Section data-testid={testIdPrefix} title="INFORMATIONS COMPLÉMENTAIRES">
      <Form>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 14,
          }}
        >
          <SectionHeader title="Autres informations" />
          <Grid padded>
            {/* OFPRA */}
            <Grid.Row
              style={{ padding: 0, paddingBottom: '1em', minHeight: 38 }}
            >
              <Grid.Column
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5em',
                }}
              >
                <label>
                  <b>
                    Placé(e) sous la protection juridique et administrative de
                    l&apos; OFPRA (réfugiée(e), apatride, bénéficaire de la
                    protection subsidiaire) :
                  </b>
                </label>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Form.Checkbox
                    radio
                    label="Oui"
                    data-testid={`${testIdPrefix}-protection-ofpra-oui`}
                    checked={protectionOfpra === true}
                    onChange={() =>
                      handlePartenaireChange('protectionOfpra', true)
                    }
                  />
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Form.Checkbox
                    radio
                    label="Non"
                    data-testid={`${testIdPrefix}-protection-ofpra-non`}
                    checked={protectionOfpra === false}
                    onChange={() =>
                      handlePartenaireChange('protectionOfpra', false)
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            {/* Height 38 suite à remarque de Florent */}
            <Grid.Row
              style={{ padding: 0, paddingBottom: '1em', minHeight: 38 }}
            >
              {/* Protection juridique */}
              <Grid.Column
                style={{
                  width: '70%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5em',
                }}
              >
                <label>
                  <b>
                    Fait l&apos;objet d&apos;une mesure de protection juridique
                    (sauvegarde de justice, curatelle, tutelle, habilitation
                    familiale, mandat de protection future) :
                  </b>
                </label>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Form.Checkbox
                    radio
                    label="Oui"
                    data-testid={`${testIdPrefix}-protection-juridique-oui`}
                    checked={protectionJuridique === true}
                    onChange={() =>
                      handlePartenaireChange('protectionJuridique', true)
                    }
                  />
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Form.Checkbox
                    radio
                    label="Non"
                    data-testid={`${testIdPrefix}-protection-juridique-non`}
                    checked={protectionJuridique === false}
                    onChange={() =>
                      handlePartenaireChange('protectionJuridique', false)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              {/* Identité vérifié */}
              <Grid.Column
                style={{
                  width: '30%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5em',
                }}
              >
                {/* @ts-ignore */}
                <Form.Field
                  required={identiteVerifieeObligatoire}
                  style={{ margin: 0 }}
                >
                  <label>A fourni un titre d&apos;identité vérifié :</label>
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        label="Oui"
                        data-testid={`${testIdPrefix}-identite-verifiee-oui`}
                        checked={identiteVerifiee === true}
                        onChange={() =>
                          handlePartenaireChange('identiteVerifiee', true)
                        }
                      />
                    }
                    type={errors?.identite ? 'error' : ''}
                    message={errors?.identite}
                  />
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        label="Non"
                        data-testid={`${testIdPrefix}-identite-verifiee-non`}
                        checked={identiteVerifiee === false}
                        onChange={() =>
                          handlePartenaireChange('identiteVerifiee', false)
                        }
                        error={!!errors?.identite}
                      />
                    }
                    type={errors?.identite ? 'error' : ''}
                    message={errors?.identite}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            {/* Etat Matrimonial */}
            <Grid.Row
              style={{
                padding: 0,
                minHeight: 50,
              }}
            >
              <Grid.Column
                style={{
                  width: '70%',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.5em',
                  alignItems: 'center',
                }}
              >
                {/* @ts-ignore */}
                <Form.Field required={inseeObligatoire} style={{ margin: 0 }}>
                  <label>Etat matrimonial antérieur (INSEE) :</label>
                </Form.Field>
                <Tooltip
                  trigger={
                    <div>
                      <Form.Group inline style={{ margin: 0 }}>
                        {OPTIONS_ETAT_MATRIMONIAL.map((option) => (
                          <Form.Radio
                            key={option.value}
                            name={`etat-matrimonial-${numeroPartenaire}`}
                            data-testid={`${testIdPrefix}-etat-matrimonial-${option.value}`}
                            value={option.value}
                            label={option.text}
                            checked={selectedEtatMatrimonial === option.value}
                            error={!!errors?.etatMatrimonial}
                            onClick={() => {
                              handleComplementInseeChange(
                                'EtatMatrimonialCode',
                                option.value,
                              );
                              handleComplementInseeChange(
                                'EtatMatrimonialLibelle',
                                option.text,
                              );
                            }}
                          />
                        ))}
                      </Form.Group>
                    </div>
                  }
                  type={errors?.etatMatrimonial ? 'error' : ''}
                  message={errors?.etatMatrimonial}
                />
                <Grid.Column
                  style={{
                    width: '30%',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.5em',
                    alignItems: 'center',
                    visibility:
                      selectedEtatMatrimonial === 'V' ||
                      selectedEtatMatrimonial === 'D'
                        ? 'visible'
                        : 'hidden',
                  }}
                >
                  {selectedEtatMatrimonial === 'V' && (
                    <>
                      {/* @ts-ignore */}
                      <Form.Field
                        required={inseeObligatoire}
                        style={{ margin: 0 }}
                      >
                        <label>Date du veuvage (INSEE):</label>
                      </Form.Field>
                      <Tooltip
                        trigger={
                          <div>
                            <DateInput
                              data-testid={`${testIdPrefix}-date-veuvage`}
                              value={dateVeuvage ? new Date(dateVeuvage) : null}
                              error={!!errors?.dateEtatMatrimonial}
                              onChange={(value) =>
                                handleInseeDateChange('DateVeuvage', value)
                              }
                            />
                          </div>
                        }
                        type={errors?.dateEtatMatrimonial ? 'error' : ''}
                        message={errors?.dateEtatMatrimonial}
                      />
                    </>
                  )}
                  {selectedEtatMatrimonial !== 'V' && (
                    <>
                      {/* @ts-ignore */}
                      <Form.Field
                        required={inseeObligatoire}
                        style={{ margin: 0 }}
                      >
                        <label>Date du divorce (INSEE):</label>
                      </Form.Field>
                      <Tooltip
                        trigger={
                          <div>
                            <DateInput
                              data-testid={`${testIdPrefix}-date-divorce`}
                              value={dateDivorce ? new Date(dateDivorce) : null}
                              error={!!errors?.dateEtatMatrimonial}
                              onChange={(value) =>
                                handleInseeDateChange('DateDivorce', value)
                              }
                            />
                          </div>
                        }
                        type={errors?.dateEtatMatrimonial ? 'error' : ''}
                        message={errors?.dateEtatMatrimonial}
                      />
                    </>
                  )}
                </Grid.Column>
              </Grid.Column>
              <Grid.Column
                style={{
                  width: '30%',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.5em',
                  alignItems: 'center',
                }}
              >
                {/* @ts-ignore */}
                <Form.Field required={inseeObligatoire} style={{ margin: 0 }}>
                  <label>Partenaire déjà pacsé par le passé (INSEE) :</label>
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        label="Oui"
                        data-testid={`${testIdPrefix}-deja-pacse-oui`}
                        checked={dejaPacse === true}
                        onChange={() =>
                          handleComplementInseeChange('DejaPacse', true)
                        }
                      />
                    }
                    type={errors?.dejaPacse ? 'error' : ''}
                    message={errors?.dejaPacse}
                  />
                </Form.Field>
                {/* @ts-ignore */}
                <Form.Field style={{ margin: 0 }}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        label="Non"
                        data-testid={`${testIdPrefix}-deja-pacse-non`}
                        checked={dejaPacse === false}
                        onChange={() =>
                          handleComplementInseeChange('DejaPacse', false)
                        }
                        error={!!errors?.dejaPacse}
                      />
                    }
                    type={errors?.dejaPacse ? 'error' : ''}
                    message={errors?.dejaPacse}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            {numeroPartenaire === 1 && (
              <Grid.Row
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}
              >
                <Grid.Column style={{ width: '23%' }}>
                  {/* @ts-ignore */}
                  <Form.Field required={inseeObligatoire} style={{ margin: 0 }}>
                    <label>
                      Nombre d&apos;enfant en commun avec le partenaire 2 :
                    </label>
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <Tooltip
                      trigger={
                        <Form.Input
                          input={{
                            'data-testid': `${testIdPrefix}-nombre-enfants`,
                          }}
                          value={nombreEnfants ?? ''}
                          error={!!errors?.nombreEnfants}
                          onChange={(_e, data) =>
                            handleNombreEnfantsChange(
                              (data?.value ?? '') as string,
                            )
                          }
                        />
                      }
                      type={errors?.nombreEnfants ? 'error' : ''}
                      message={errors?.nombreEnfants}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </div>
      </Form>
    </Section>
  );
};

export default InformationsComplementaires;
